import { useState } from 'react'
import { MessageSquare, Plus, Search, Ticket as TicketIcoon } from 'lucide-react'
import type { Ticket, TicketBron } from '../../../gedeeld/types'
import { api, type TicketFilter } from '../../lib/api'
import { useLaad } from '../../lib/laden'
import { geleden } from '../../lib/opmaak'
import { Link, useRoute, useTitel, zetParam } from '../../lib/router'
import { meldProbleem, useStaat, vestigingNaam } from '../../lib/staat'
import { BRON, categorie, reactie, ticketBron, ticketNummer } from '../../lib/woorden'
import { PrioPil, StatusPil } from '../stukjes'
import { Avatar, Foutvlak, Knop, Laden, Leeg, Tabs } from '../ui'

/* ===========================================================================
 *  De tickets
 *
 *  Een medewerker ziet hier alleen zijn eigen tickets (de database laat
 *  niets anders door). IT ziet alles, met de tabbladen die bij het werk
 *  horen: wat open staat, wat van mij is, wat nog niemand heeft.
 *
 *  Sinds het dashboard zijn meldingen hierheen stuurt, lopen app-meldingen
 *  en echte IT-storingen door elkaar. Daarom filtert IT er ook op bron.
 *  Dat zijn bewust GEEN extra tabbladen: een tabblad gaat hier over de
 *  stand van het werk (open, voor mij, nog niemand) en de server kent die
 *  filters. Bron is een tweede, dwarse vraag -- "open" én "uit het
 *  dashboard" moeten samen kunnen. Filterpillen doen dat al zo in de kluis
 *  en de inventaris, en ze draaien hier over de lijst die er toch al is:
 *  geen extra vraag aan de server, geen wijziging aan het antwoord.
 *
 *  De kanttekening: de server geeft hoogstens 500 tickets per tabblad
 *  terug. De tellingen in de pillen gaan dus over wat binnenkwam, niet over
 *  alles wat ooit gemeld is. Voor "wat ligt er nu" klopt dat; moet het ooit
 *  over het hele archief gaan, dan hoort het filter alsnog naar de server.
 * =========================================================================== */

export default function Tickets() {
  const beheerder = useStaat((s) => s.beheerder)
  const { q: zoek } = useRoute()
  const filter = (zoek.get('f') as TicketFilter | null) ?? 'open'
  /* Alleen IT filtert op bron. Voor een medewerker bestaat dit niet, ook
     niet als hij ?b= in het adres plakt; en een bron die we niet kennen
     wordt null in plaats van een filter dat alles wegstreept. */
  const bronF = beheerder ? BRON.find((b) => b.key === zoek.get('b'))?.key ?? null : null
  const [q, setQ] = useState('')
  const [vraag, setVraag] = useState('')
  const { data, fout, laden, opnieuw } = useLaad(() => api.tickets(filter, vraag || undefined), [filter, vraag])
  useTitel(beheerder ? 'Tickets' : 'Mijn tickets')

  /* Hoeveel er van elke bron binnenkwamen, geteld over het hele antwoord --
     dus de pil "Uit het dashboard 12" blijft 12 zeggen als je hem aanklikt. */
  const telling = new Map<TicketBron, number>()
  for (const t of data ?? []) telling.set(t.bron, (telling.get(t.bron) ?? 0) + 1)
  const lijst = bronF ? (data ?? []).filter((t) => t.bron === bronF) : data ?? []
  /* Eén soort bron is geen keuze; dan is de rij pillen alleen maar ruis.
     Staat er wél een filter aan, dan blijft de rij staan -- anders kun je
     hem niet meer uitzetten en lijkt het scherm zonder reden leeg. */
  const toonBronnen = beheerder && (telling.size > 1 || bronF !== null)

  const tabs: { key: TicketFilter; label: string }[] = beheerder
    ? [
      { key: 'open', label: 'Open' },
      { key: 'voor_mij', label: 'Voor mij' },
      { key: 'niet_toegewezen', label: 'Nog niemand' },
      { key: 'afgerond', label: 'Afgerond' },
      { key: 'alles', label: 'Alles' },
    ]
    : [
      { key: 'open', label: 'Lopend' },
      { key: 'afgerond', label: 'Afgerond' },
      { key: 'alles', label: 'Alles' },
    ]

  return (
    <div className="pagina">
      <div className="pagina-kop">
        <div>
          <h1><TicketIcoon size={20} /> {beheerder ? 'Tickets' : 'Mijn tickets'}</h1>
          <p>{beheerder
            ? 'Alles wat gemeld is. Nieuw en spoed bovenaan.'
            : 'Wat je bij IT hebt gemeld, en hoe ver het is. Je krijgt een seintje in het dashboard als er iets verandert.'}</p>
        </div>
        <Knop soort="hoofd" icoon={<Plus size={16} />} onClick={() => meldProbleem()}>Probleem melden</Knop>
      </div>

      <div className="werkbalk">
        <Tabs<TicketFilter> waarde={filter} tabs={tabs} onKies={(f) => zetParam('f', f === 'open' ? null : f)} />
        {toonBronnen && (
          <>
            <button type="button" className={`filter-pil ${!bronF ? 'actief' : ''}`} onClick={() => zetParam('b', null)}>
              Alle bronnen
            </button>
            {BRON.filter((b) => telling.has(b.key) || b.key === bronF).map((b) => (
              <button key={b.key} type="button" className={`filter-pil ${bronF === b.key ? 'actief' : ''}`}
                title={`Alleen wat ${b.hoe} binnenkwam`}
                onClick={() => zetParam('b', bronF === b.key ? null : b.key)}>
                {b.label} <span className="stil">{telling.get(b.key) ?? 0}</span>
              </button>
            ))}
          </>
        )}
        <span className="vul" />
        <form className="zoekveld" onSubmit={(e) => { e.preventDefault(); setVraag(q.trim()) }}>
          <Search size={15} />
          <input value={q} onChange={(e) => { setQ(e.target.value); if (!e.target.value) setVraag('') }}
            placeholder="Zoek of typ een nummer…" />
        </form>
      </div>

      {fout && <Foutvlak fout={fout} opnieuw={opnieuw} />}
      {laden && !data && <Laden />}
      {data && lijst.length === 0 && (
        <Leeg icoon={<TicketIcoon />}
          titel={bronF ? 'Niets uit deze bron' : vraag ? 'Niets gevonden' : filter === 'open' ? 'Niets open' : 'Geen tickets'}
          tekst={bronF ? `In dit tabblad staat niets dat ${ticketBron(bronF).hoe} binnenkwam.`
            : vraag ? `Geen ticket met “${vraag}”.` : beheerder ? 'Er ligt niets. Mooi.' : 'Doet er iets het niet, of heb je iets nodig? Meld het hier.'}>
          {!vraag && !bronF && <Knop soort="hoofd" icoon={<Plus size={16} />} onClick={() => meldProbleem()}>Probleem melden</Knop>}
        </Leeg>
      )}
      {data && lijst.length > 0 && <TicketLijst tickets={lijst} beheerder={beheerder} />}
    </div>
  )
}

export function TicketLijst({ tickets, beheerder, compact }: { tickets: Ticket[]; beheerder: boolean; compact?: boolean }) {
  return (
    <div className={`ticket-lijst ${compact ? 'compact' : ''}`}>
      {tickets.map((t) => (
        <Link key={t.id} naar={`/t/${t.id}`} className={`ticket-regel prio-${t.prioriteit} ${t.status === 'nieuw' && beheerder ? 'is-nieuw' : ''}`}>
          <span className="ticket-nummer mono">{ticketNummer(t.nummer)}<BronIcoon bron={t.bron} /></span>
          <span className="ticket-hoofd">
            <span className="ticket-titel afkap">{t.titel}</span>
            <span className="ticket-sub afkap">
              {[beheerder ? t.melderNaam : null, categorie(t.categorie), vestigingNaam(t.vestigingId), t.itemNaam,
                t.externNummer ? `${t.externPartij ?? 'extern'} #${t.externNummer}` : null].filter(Boolean).join(' · ')}
            </span>
          </span>
          <PrioPil p={t.prioriteit} alleenAfwijkend />
          <StatusPil status={t.status} />
          <Reactie t={t} beheerder={beheerder} />
          {!compact && (
            <span className="ticket-wie" title={t.toegewezenNaam ? `Bij ${t.toegewezenNaam}` : 'Nog bij niemand'}>
              {t.toegewezenNaam ? <Avatar naam={t.toegewezenNaam} id={t.toegewezenAan} grootte={22} /> : <span className="avatar-leeg" />}
            </span>
          )}
          {!compact && (
            <span className="ticket-tel stil" title="Reacties">
              {t.berichten ? <><MessageSquare size={13} /> {t.berichten}</> : null}
            </span>
          )}
          <span className="ticket-wanneer stil">{geleden(t.laatsteAt)}</span>
        </Link>
      ))}
    </div>
  )
}

/**
 * Hoe het binnenkwam, als het niet via het scherm was.
 *
 * Het icoon komt uit BRON (woorden.ts), zodat een nieuwe bron hier niet
 * vergeten kan worden: wie daar een regel bijzet, ziet hem meteen staan.
 */
export function BronIcoon({ bron }: { bron: TicketBron }) {
  const b = ticketBron(bron)
  if (!b.icoon) return null
  const Icoon = b.icoon
  return <Icoon size={11} className="bron" aria-label={b.hoe} />
}

/** Nog zoveel tijd, of te laat. Alleen voor IT, en alleen zolang niemand hem oppakte. */
function Reactie({ t, beheerder }: { t: Ticket; beheerder: boolean }) {
  if (!beheerder || t.opgepaktAt) return <span />
  const r = reactie(t)
  return r ? <span className={`reactie t-${r.kleur}`} title="Hoe lang IT nog heeft om hem op te pakken">{r.tekst}</span> : <span />
}
